import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminActions,
  getAdminMetrics,
  type AdminActionLogItem,
  type AdminActionLogPage,
  type AdminActionLogParams,
  type AdminMetrics,
} from "@/lib/api-client";
import { toast } from "sonner";
import { formatTimestamp, formatCurrency } from "@/lib/formatters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ShieldCheck, Filter } from "lucide-react";

const ACTION_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "all", labelKey: "filters.actions.all" },
  { value: "search_basic", labelKey: "filters.actions.basicSearch" },
  { value: "search_extended", labelKey: "filters.actions.extendedSearch" },
  { value: "csv_export", labelKey: "filters.actions.csvExport" },
  {
    value: "gewerbegebiet",
    labelKey: "filters.actions.industrialAreaSearch",
  },
  { value: "deep_enrichment", labelKey: "filters.actions.deepEnrichment" },
  { value: "search_feedback", labelKey: "filters.actions.feedback" },
];

const COST_FILTER_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "all", labelKey: "filters.costs.all" },
  { value: "paid", labelKey: "filters.costs.paid" },
  { value: "free", labelKey: "filters.costs.free" },
];

function formatEur(value: number): string {
  return formatCurrency(value, "EUR");
}

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const AdminDashboardPage = () => {
  const { t } = useTranslation("admin");
  const { profile } = useAuth();

  // Filters
  const [actionCode, setActionCode] = useState("all");
  const [costFilter, setCostFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(() => toDateInputValue(new Date()));
  const [page, setPage] = useState(1);

  // Data
  const [data, setData] = useState<AdminActionLogPage | null>(null);
  const [loading, setLoading] = useState(true);

  // KPI metrics
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [actionCode, costFilter, dateFrom, dateTo]);

  // Fetch KPI metrics (full-dataset totals, independent of filters).
  // Owner-gated: only fetch once profile is loaded AND the user is an owner.
  // Avoids an unnecessary 403 + spurious metricsLoadFailed toast for non-owners
  // and while profile is still null/undefined before the early-return below.
  useEffect(() => {
    if (!profile?.is_owner) return;

    let cancelled = false;
    setMetricsLoading(true);

    getAdminMetrics()
      .then((result) => {
        if (!cancelled) setMetrics(result);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("errors.metricsLoadFailed"));
      })
      .finally(() => {
        if (!cancelled) setMetricsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profile?.is_owner]);

  // Fetch action log
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params: AdminActionLogParams = { page, per_page: 50 };
    if (actionCode !== "all") params.action_code = actionCode;
    if (dateFrom) params.date_from = `${dateFrom}T00:00:00Z`;
    if (dateTo) params.date_to = `${dateTo}T23:59:59Z`;

    getAdminActions(params)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("errors.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, actionCode, dateFrom, dateTo]);

  // Client-side cost filter (paid/free) applied on top of server data
  const filteredItems = useMemo(() => {
    if (!data) return [];
    if (costFilter === "paid")
      return data.items.filter((i) => i.credit_cost > 0);
    if (costFilter === "free")
      return data.items.filter((i) => i.credit_cost === 0);
    return data.items;
  }, [data, costFilter]);

  // Summary calculations over visible (filtered) items
  const summary = useMemo(() => {
    let totalRevenue = 0;
    let totalOwnerCost = 0;
    for (const item of filteredItems) {
      totalRevenue += item.credit_cost_eur;
      totalOwnerCost += item.owner_cost_eur;
    }
    return {
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalOwnerCost: Math.round(totalOwnerCost * 100) / 100,
      netMargin: Math.round((totalRevenue - totalOwnerCost) * 100) / 100,
    };
  }, [filteredItems]);

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.per_page))
    : 1;

  if (!profile?.is_owner) {
    return (
      <div className="container max-w-4xl py-8">
        <p className="text-muted-foreground">{t("errors.accessDenied")}</p>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{t("title")}</h1>
          <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
        </div>
      </div>

      {/* KPI cards — full-dataset totals (not page-scoped) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {/* Count cards */}
        {(
          [
            { labelKey: "kpi.companies", value: metrics?.counts.companies },
            { labelKey: "kpi.users", value: metrics?.counts.users },
            { labelKey: "kpi.searches", value: metrics?.counts.searches },
          ] as { labelKey: string; value: number | undefined }[]
        ).map(({ labelKey, value }) => (
          <div
            key={labelKey}
            className="bg-card border border-border rounded-[10px] p-4 space-y-1"
          >
            <p className="text-xs text-muted-foreground">{t(labelKey)}</p>
            <p className="text-2xl font-bold tabular-nums">
              {metricsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : value !== undefined ? (
                value.toLocaleString()
              ) : (
                "—"
              )}
            </p>
          </div>
        ))}
        {/* Financial cards */}
        {(
          [
            {
              labelKey: "kpi.revenue",
              value: metrics?.financials.revenue_eur,
              colorClass: "text-green-600",
            },
            {
              labelKey: "kpi.ownerCost",
              value: metrics?.financials.owner_cost_eur,
              colorClass: "text-red-600",
            },
            {
              labelKey: "kpi.margin",
              value: metrics?.financials.margin_eur,
              colorClass:
                (metrics?.financials.margin_eur ?? 0) >= 0
                  ? "text-green-600"
                  : "text-red-600",
            },
          ] as { labelKey: string; value: number | undefined; colorClass: string }[]
        ).map(({ labelKey, value, colorClass }) => (
          <div
            key={labelKey}
            className="bg-card border border-border rounded-[10px] p-4 space-y-1"
          >
            <p className="text-xs text-muted-foreground">{t(labelKey)}</p>
            <p className={`text-2xl font-bold tabular-nums ${colorClass}`}>
              {metricsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : value !== undefined ? (
                formatEur(value)
              ) : (
                "—"
              )}
            </p>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground -mt-2">{t("kpi.totalsNote")}</p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-card border border-border rounded-[10px] p-4">
        <Filter className="h-4 w-4 text-muted-foreground mt-1" />

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {t("filters.dateFrom")}
          </label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-40"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {t("filters.dateTo")}
          </label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-40"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {t("filters.action")}
          </label>
          <Select value={actionCode} onValueChange={setActionCode}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {t("filters.cost")}
          </label>
          <Select value={costFilter} onValueChange={setCostFilter}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COST_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Action log table */}
      <div className="bg-card border border-border rounded-[10px] overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm p-6">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("common.loading")}</span>
          </div>
        ) : filteredItems.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">{t("emptyState")}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                      {t("table.timestamp")}
                    </th>
                    <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                      {t("table.company")}
                    </th>
                    <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                      {t("table.user")}
                    </th>
                    <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                      {t("table.action")}
                    </th>
                    <th className="px-3 py-3 text-right font-medium text-muted-foreground">
                      {t("table.userCost")}
                    </th>
                    <th className="px-3 py-3 text-right font-medium text-muted-foreground">
                      {t("table.myCost")}
                    </th>
                    <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                      {t("table.ref")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item, idx) => (
                    <tr
                      key={`${item.timestamp}-${idx}`}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(item.timestamp)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {item.company_name}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {item.user_name}
                      </td>
                      <td className="px-3 py-2.5">{item.action_name_de}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                        {item.credit_cost > 0 ? (
                          `${item.credit_cost} Credits (${formatEur(item.credit_cost_eur)})`
                        ) : (
                          <span className="text-muted-foreground">
                            {t("table.free")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                        {formatEur(item.owner_cost_eur)}
                      </td>
                      <td
                        className="px-3 py-2.5 text-muted-foreground text-xs font-mono truncate max-w-[120px]"
                        title={item.reference_id ?? ""}
                      >
                        {item.reference_id
                          ? item.reference_id.slice(0, 12)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary row */}
            <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 border-t border-border bg-muted/20 text-sm">
              <div className="flex gap-6">
                <span>
                  <span className="text-muted-foreground">
                    {t("summary.revenue")}
                  </span>
                  <span className="font-semibold text-green-600">
                    {formatEur(summary.totalRevenue)}
                  </span>
                </span>
                <span>
                  <span className="text-muted-foreground">
                    {t("summary.costs")}
                  </span>
                  <span className="font-semibold text-red-600">
                    {formatEur(summary.totalOwnerCost)}
                  </span>
                </span>
                <span>
                  <span className="text-muted-foreground">
                    {t("summary.margin")}
                  </span>
                  <span
                    className={`font-semibold ${summary.netMargin >= 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {formatEur(summary.netMargin)}
                  </span>
                </span>
              </div>

              {/* Pagination */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t("pagination.info", {
                    page: data?.page ?? 1,
                    totalPages,
                    total: data?.total ?? 0,
                  })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  {t("pagination.back")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                >
                  {t("pagination.next")}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
